import type { ExternalHref } from "@qendo/util";
import { type Href, router } from "expo-router";
import { type ReactNode, useCallback } from "react";
import { Pressable, type PressableProps, StyleSheet, View, type ViewStyle } from "react-native";
import type { ColorValue, StyleProp } from "react-native/Libraries/StyleSheet/StyleSheet";
import type { GestureResponderEvent } from "react-native/Libraries/Types/CoreEventTypes";
import { Badge, type BadgeSize, type BadgeVariant } from "~/components/feedback/Badge";
import { BaseText, Subtitle } from "~/components/layout/text";
import { Nothing } from "~/components/permission/Nothing";
import { Only } from "~/components/permission/Only";
import { Space } from "~/constants/sizing";
import { useTheme } from "~/providers/ThemeProvider";
import { empty, filled } from "~/util";
import { isExternalLink, openLink } from "~/util/browser";
import { QendoIcon, type QendoIconSet } from "../QendoIcon";

export type ListingItemProps = {
    title?: string;
    icon?: QendoIconSet;
    left?: () => ReactNode;
    subtitle?: ReactNode;
    children?: ReactNode;
    hideChevron?: boolean;
    badge?: string | null;
    badgeVariant?: BadgeVariant;
    badgeSize?: BadgeSize;
    colour?: ColorValue;
    backgroundColor?: ColorValue;
    last?: boolean;
    href?: Href | ExternalHref;
    right?: () => ReactNode;
    style?: StyleProp<ViewStyle> | null;
    hide?: boolean | null;
    replace?: boolean;
} & PressableProps;

export const ListingItem = ({
    title,
    icon,
    left,
    subtitle,
    children,
    hideChevron,
    badge,
    last,
    href,
    right,
    colour,
    backgroundColor,
    style,
    onPress,
    hide,
    replace,
    badgeVariant = "info",
    badgeSize = "sm",
    ...pressableProps
}: ListingItemProps) => {
    const { colours, background } = useTheme();

    const handlePress = useCallback(
        (event: GestureResponderEvent) => {
            href && (isExternalLink(href) ? openLink(href) : replace ? router.replace(href) : router.navigate(href));
            onPress?.(event);
        },
        [onPress, href, replace],
    );

    const accessibilityRole =
        pressableProps.accessibilityRole ??
        ((isExternalLink(href) && "link") ||
            (filled(href) && "menuitem") ||
            (filled(onPress) && "button") ||
            undefined);

    if (hide) {
        return <Nothing />;
    }

    return (
        <Pressable
            {...pressableProps}
            onPress={handlePress}
            style={styles.pressable}
            accessibilityRole={accessibilityRole}
        >
            <View
                style={[
                    styles.container,
                    background.primary,
                    last ? null : { borderBottomWidth: 2, borderColor: colours.border.primary },
                    style,
                ]}
            >
                {left?.()}
                {icon && (
                    <QendoIcon name={icon} size={24} style={styles.icon} color={colour ?? colours.icon.secondary} />
                )}
                <View style={[styles.body, { backgroundColor }]}>
                    <Only when={title || badge}>
                        <View style={styles.title}>
                            {title && (
                                <BaseText size="base" style={colour ? { color: colour } : undefined}>
                                    {title}
                                </BaseText>
                            )}
                            {filled(badge) && <Badge title={badge} variant={badgeVariant} size={badgeSize} />}
                        </View>
                    </Only>
                    {filled(subtitle) && <Subtitle size="md">{subtitle}</Subtitle>}
                    {children}
                </View>
                {empty(hideChevron || right) && (
                    <QendoIcon name="chevron-right" size={24} style={styles.icon} color={colour} />
                )}
                {right?.()}
            </View>
        </Pressable>
    );
};

const styles = StyleSheet.create({
    pressable: {
        width: "100%",
    },
    container: {
        borderWidth: 0,
        flexDirection: "row",
        alignItems: "flex-start",
        columnGap: Space.xs,
        padding: Space.md,
    },
    body: {
        alignItems: "stretch",
        position: "relative",
        flexGrow: 1,
        flexShrink: 1,
        rowGap: Space.xxx,
    },
    title: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        width: "100%",
    },
    icon: {
        width: Space.lg,
        height: Space.lg,
        flexShrink: 0,
        flexGrow: 0,
    },
});
